<template>
  <v-card outlined class="payment-card">
    <v-card-text class="pa-4">
      <div class="payment-header">
        <div>
          <div class="section-title">Payments</div>
          <div class="section-subtitle">
            {{ payments.length }} customer{{ payments.length === 1 ? "" : "s" }}
          </div>
        </div>

        <DateTimePicker
          defaultRange="today"
          :range="true"
          v-model="dateTime"
          @submit="getAll()"
        />
      </div>

      <div class="total-box mt-4">
        <div>
          <div class="total-label">Total Collected</div>
          <div class="total-value">{{ total | currency }}</div>
        </div>

        <v-icon color="green darken-2">mdi-cash-check</v-icon>
      </div>

      <v-skeleton-loader
        v-if="isLoading"
        type="list-item-two-line, list-item-two-line, list-item-two-line"
        class="mt-4"
      />

      <div v-else-if="payments.length > 0" class="payment-list mt-4">
        <div
          v-for="payment in payments"
          :key="payment.id || payment.name"
          class="payment-row"
        >
          <div class="payment-main">
            <div class="payment-avatar">
              {{ getInitial(payment.name) }}
            </div>

            <div>
              <div class="payment-name">
                {{ payment.name || "Unknown Customer" }}
              </div>

              <div class="payment-subtitle">
                Payment collected
              </div>
            </div>
          </div>

          <div class="payment-total">
            {{ payment.totalPayments | currency }}
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <v-icon size="52" color="grey lighten-1">
          mdi-cash-remove
        </v-icon>

        <div class="empty-title">No payment collected</div>
        <div class="empty-subtitle">Try changing the date range.</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import paymentService from "@/modules/Customer/Payment/service";

const DateTimePicker = () => import("@/components/DateTimePicker");

export default {
  name: "TodayPayment",

  components: {
    DateTimePicker,
  },

  data() {
    return {
      isLoading: true,
      payments: [],
      dateTime: {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
    };
  },

  computed: {
    total() {
      return this.payments.reduce((sum, row) => {
        return sum + Number(row.totalPayments || 0);
      }, 0);
    },
  },

  mounted() {
    this.getAll();
  },

  methods: {
    getInitial(name) {
      if (!name) {
        return "?";
      }

      return name.charAt(0).toUpperCase();
    },

    async getAll() {
      this.isLoading = true;

      const where = {
        start: this.dateTime.start,
        end: this.dateTime.end,
      };

      try {
        const result = await paymentService.groupByCustomer(where);
        this.payments = result.data || [];
      } catch (error) {
        console.error("groupByCustomer failed", error);
        this.payments = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.payment-card {
  border-radius: 20px;
  background: #ffffff;
}

.payment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.total-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.total-label {
  font-size: 12px;
  color: #64748b;
}

.total-value {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.payment-list {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.payment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 12px;
  margin-bottom: 10px;
}

.payment-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.payment-avatar {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 14px;
  background: #f0fdf4;
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.payment-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  text-transform: capitalize;
}

.payment-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.payment-total {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
}

.empty-state {
  text-align: center;
  padding: 34px 0;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 10px;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}
</style>